import { lazy, Suspense } from 'react'
import { Routes, Route } from 'react-router-dom'
import Layout from './components/Layout'
import ProtectedRoute from './components/ProtectedRoute'
import AdminRoute from './components/AdminRoute'
import PublicOnlyRoute from './components/PublicOnlyRoute'
import LoadingSpinner from './components/LoadingSpinner'

// Eager: landing page (must be instant)
import Home from './pages/Home'

// Lazy: public pages
const Features = lazy(() => import('./pages/Features'))
const Contact = lazy(() => import('./pages/Contact'))
const Accessibility = lazy(() => import('./pages/Accessibility'))
const Privacy = lazy(() => import('./pages/Privacy'))
const Terms = lazy(() => import('./pages/Terms'))
const Sitemap = lazy(() => import('./pages/Sitemap'))
const About = lazy(() => import('./pages/About'))
const ForLearners = lazy(() => import('./pages/ForLearners'))
const ForAdmins = lazy(() => import('./pages/ForAdmins'))
const SignIn = lazy(() => import('./pages/SignIn'))
const SignUp = lazy(() => import('./pages/SignUp'))
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'))
const CertificateVerification = lazy(() => import('./pages/CertificateVerification'))
const NotFound = lazy(() => import('./pages/NotFound'))

// Lazy: learner pages
const Dashboard = lazy(() => import('./pages/Dashboard'))
const Catalog = lazy(() => import('./pages/Catalog'))
const CoursePlayer = lazy(() => import('./pages/CoursePlayer'))
const Transcript = lazy(() => import('./pages/Transcript'))
const Support = lazy(() => import('./pages/Support'))
const Settings = lazy(() => import('./pages/Settings'))

// Lazy: admin pages
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'))
const AdminLogins = lazy(() => import('./pages/admin/AdminLogins'))
const AdminEmails = lazy(() => import('./pages/admin/AdminEmails'))
const AdminAiGenerationLog = lazy(() => import('./pages/admin/AdminAiGenerationLog'))
const AdminCourseDetail = lazy(() => import('./pages/admin/AdminCourseDetail'))
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'))
const AdminUserEdit = lazy(() => import('./pages/admin/AdminUserEdit'))
const AdminUserTranscript = lazy(() => import('./pages/admin/AdminUserTranscript'))
const AdminTrainings = lazy(() => import('./pages/admin/AdminTrainings'))
const AdminCourses = lazy(() => import('./pages/admin/AdminCourses'))
const AdminAssessments = lazy(() => import('./pages/admin/AdminAssessments'))
const AdminSatisfactionMerged = lazy(() => import('./pages/admin/AdminSatisfactionMerged'))
const AdminOrganizations = lazy(() => import('./pages/admin/AdminOrganizations'))
const AdminCompliance = lazy(() => import('./pages/admin/AdminCompliance'))
const AdminOrgEdit = lazy(() => import('./pages/admin/AdminOrgEdit'))
const AdminAiModernize = lazy(() => import('./pages/admin/AdminAiModernize'))
const AdminAiModernizeReview = lazy(() => import('./pages/admin/AdminAiModernizeReview'))

function App() {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        <Route element={<Layout />}>
          {/* Public routes */}
          <Route index element={<PublicOnlyRoute><Home /></PublicOnlyRoute>} />
          <Route path="features" element={<Features />} />
          <Route path="contact" element={<Contact />} />
          <Route path="accessibility" element={<Accessibility />} />
          <Route path="privacy" element={<Privacy />} />
          <Route path="terms" element={<Terms />} />
          <Route path="sitemap" element={<Sitemap />} />
          <Route path="about" element={<About />} />
          <Route path="for-learners" element={<ForLearners />} />
          <Route path="for-administrators" element={<ForAdmins />} />
          <Route path="signin" element={<PublicOnlyRoute><SignIn /></PublicOnlyRoute>} />
          <Route path="signup" element={<PublicOnlyRoute><SignUp /></PublicOnlyRoute>} />
          <Route path="forgot-password" element={<PublicOnlyRoute><ForgotPassword /></PublicOnlyRoute>} />

          {/* Catalog — viewable by anyone */}
          <Route path="catalog" element={<Catalog />} />

          {/* Public certificate verification */}
          <Route path="verify/:certificateId" element={<CertificateVerification />} />

          {/* Protected — learner + admin */}
          <Route path="dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="course/:slug" element={<ProtectedRoute><CoursePlayer /></ProtectedRoute>} />
          <Route path="transcript" element={<ProtectedRoute><Transcript /></ProtectedRoute>} />
          <Route path="support" element={<ProtectedRoute><Support /></ProtectedRoute>} />
          <Route path="account" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          <Route path="settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />

          {/* Admin only */}
          <Route path="admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
          <Route path="admin/logins" element={<AdminRoute><AdminLogins /></AdminRoute>} />
          <Route path="admin/emails" element={<AdminRoute><AdminEmails /></AdminRoute>} />
          {/* Intentionally not linked from any nav — reachable only by an
              admin who knows the URL. See AdminAiGenerationLog.tsx header. */}
          <Route path="admin/ai-generation-log" element={<AdminRoute><AdminAiGenerationLog /></AdminRoute>} />
          <Route path="admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
          <Route path="admin/users/:userId" element={<AdminRoute><AdminUserEdit /></AdminRoute>} />
          <Route path="admin/users/:userId/transcript" element={<AdminRoute><AdminUserTranscript /></AdminRoute>} />
          <Route path="admin/trainings" element={<AdminRoute><AdminTrainings /></AdminRoute>} />
          <Route path="admin/trainings/:courseId" element={<AdminRoute><AdminCourseDetail /></AdminRoute>} />
          <Route path="admin/courses" element={<AdminRoute><AdminCourses /></AdminRoute>} />
          <Route path="admin/assessments" element={<AdminRoute><AdminAssessments /></AdminRoute>} />
          <Route path="admin/satisfaction" element={<AdminRoute><AdminSatisfactionMerged /></AdminRoute>} />
          <Route path="admin/organizations" element={<AdminRoute><AdminOrganizations /></AdminRoute>} />
          <Route path="admin/organizations/:orgId" element={<AdminRoute><AdminOrgEdit /></AdminRoute>} />
          <Route path="admin/compliance" element={<AdminRoute><AdminCompliance /></AdminRoute>} />
          <Route path="admin/ai-modernize" element={<AdminRoute><AdminAiModernize /></AdminRoute>} />
          <Route path="admin/ai-modernize/:courseId" element={<AdminRoute><AdminAiModernizeReview /></AdminRoute>} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </Suspense>
  )
}

export default App
